import React, { useState, useEffect, useRef } from 'react';
import { 
  ChatSession, 
  ChatMessage, 
  ChatContext, 
  Exercise, 
  ExerciseFeedback,
  ConceptCategory 
} from '@unity-learning/shared';
import { ChatService } from '../services/chatService';
import { useUser } from '../hooks/useUser';
import { useLearningMode } from '../contexts/LearningModeContext';
import { trackEvent } from '../utils/analytics';
import { formatExerciseTitle } from '../utils/formatExerciseTitle';
import ReactMarkdown from 'react-markdown';
import './PedagogicalChatbot.css';

interface PedagogicalChatbotProps {
  exercise: Exercise;
  currentCode: string;
  feedback?: {
    success: boolean;
    message: string;
    suggestions?: string[];
    detailedFeedback?: {
      score: number;
      compilationStatus: {
        wouldCompile: boolean;
        errors: string[];
        warnings: string[];
      };
      conceptMastery: Record<string, number>;
      correctSolution?: string;
      whatWorked?: string[];
      keyIssues?: string[];
      learningInsights?: string[];
      nextSteps?: string[];
    };
  };
  miniCoursGenerated?: boolean;
  correctionViewed?: boolean;
  isOpen: boolean;
  onClose: () => void;
  mode?: 'new' | 'review';
}

export const PedagogicalChatbot: React.FC<PedagogicalChatbotProps> = ({
  exercise,
  currentCode,
  feedback,
  miniCoursGenerated = false,
  correctionViewed = false,
  isOpen,
  onClose,
  mode = 'new'
}) => {
  const [session, setSession] = useState<ChatSession | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [suggestionsCollapsed, setSuggestionsCollapsed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // B4 — mode « explication guidée » (dernier recours) : state local, envoyé comme flag au backend à chaque message
  const [guidedMode, setGuidedMode] = useState(false);

  const { user } = useUser();
  const { learningMode, programmingLanguage, isPythonJsMode, isGodotMode } = useLearningMode();

  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Scroll the messages container itself (scrollIntoView would also scroll the page behind the panel)
  const scrollToBottom = () => {
    const container = messagesContainerRef.current;
    if (container) {
      container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading]);

  // Auto-grow the input textarea with its content (capped by max-height in CSS)
  useEffect(() => {
    const textarea = inputRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      // +2px : bordures haut/bas (box-sizing border-box), sinon une scrollbar apparaît au repos
      textarea.style.height = `${Math.min(textarea.scrollHeight + 2, 120)}px`;
    }
  }, [currentMessage]);

  // Initialize chat session when component opens (if not already preloaded)
  useEffect(() => {
    if (isOpen && !session) {
      initializeSession();
    }
  }, [isOpen]);

  // Reset session when exercise changes
  useEffect(() => {
    setSession(null);
    setMessages([]);
    setError(null);
    setSuggestions([]);
    setGuidedMode(false);
  }, [exercise.id]);

  // Preload session data when component mounts (even if closed)
  useEffect(() => {
    if (!session && user?.id) {
      const preloadSession = async () => {
        // Retry logic for preload
        for (let attempt = 1; attempt <= 2; attempt++) {
          try {
            const chatSession = await ChatService.getOrCreateSession(exercise.id);
            setSession(chatSession);
            setMessages(chatSession.messages);
            
            if (chatSession.messages.length === 0) {
              generateInitialSuggestions();
            } else {
              const lastAssistantMessage = chatSession.messages
                .filter(msg => msg.role === 'assistant')
                .pop();
              if (lastAssistantMessage && lastAssistantMessage.suggestions) {
                setSuggestions(lastAssistantMessage.suggestions);
              } else {
                generateInitialSuggestions();
              }
            }
            return; // Success, exit retry loop
          } catch (err) {
            console.log(`Preload session attempt ${attempt} failed:`, err);
            if (attempt === 2) {
              console.log('Preload failed after 2 attempts, will retry when chatbot opens');
            } else {
              // Wait 1 second before retry
              await new Promise(resolve => setTimeout(resolve, 1000));
            }
          }
        }
      };
      
      preloadSession();
    }
  }, [exercise.id, user]);

  // Manage body class for layout adjustment
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('chatbot-open');
      inputRef.current?.focus();
      scrollToBottom();
    } else {
      document.body.classList.remove('chatbot-open');
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove('chatbot-open');
    };
  }, [isOpen]);



  const initializeSession = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      if (!user?.id) {
        setError('Vous devez être connecté pour utiliser le chat');
        return;
      }
      
      console.log('🔍 Initializing chat session for exercise:', exercise.id);
      const chatSession = await ChatService.getOrCreateSession(exercise.id);
      
      setSession(chatSession);
      setMessages(chatSession.messages);
      
      // Generate initial suggestions if no messages exist
      if (chatSession.messages.length === 0) {
        generateInitialSuggestions();
      } else {
        // If there are existing messages, get suggestions from the last assistant message
        const lastAssistantMessage = chatSession.messages
          .filter(msg => msg.role === 'assistant')
          .pop();
        if (lastAssistantMessage && lastAssistantMessage.suggestions) {
          setSuggestions(lastAssistantMessage.suggestions);
        } else {
          generateInitialSuggestions();
        }
      }
    } catch (err) {
      console.error('❌ Chat initialization failed:', err);
      setError(err instanceof Error ? err.message : 'Erreur lors de l\'initialisation du chat');
    } finally {
      setIsLoading(false);
    }
  };

  const generateInitialSuggestions = () => {
    const initialSuggestions = mode === 'review' ? [
      "Peux-tu expliquer ma solution ?",
      "Quels concepts ai-je bien appliqués ?",
      "Comment aurais-je pu améliorer mon code ?",
      "Quelles sont les bonnes pratiques utilisées ?",
      "Que puis-je apprendre de cet exercice ?"
    ] : [
      "Comment commencer cet exercice ?",
      "Peux-tu expliquer les concepts de base ?",
      "Quelles sont les étapes à suivre ?",
      "J'ai une erreur, peux-tu m'aider ?",
      "Comment améliorer mon code ?"
    ];
    setSuggestions(initialSuggestions);
  };

  const buildChatContext = (): ChatContext => {
    // Convert local feedback format to ChatContext format
    const contextFeedback = feedback ? {
      id: 'temp-id',
      attemptId: 'temp-attempt-id',
      logicAnalysis: feedback.message,
      whatWorked: feedback.detailedFeedback?.whatWorked || [],
      keyIssues: feedback.detailedFeedback?.keyIssues || [],
      learningInsights: feedback.detailedFeedback?.learningInsights || [],
      nextSteps: feedback.detailedFeedback?.nextSteps || [],
      // Sans correction détaillée (échec de la correction), pas de note : le Coach ne doit pas lire « 0/100 ».
      overallScore: feedback.detailedFeedback ? feedback.detailedFeedback.score : undefined,
      compilationStatus: feedback.detailedFeedback?.compilationStatus || {
        wouldCompile: true,
        errors: [],
        warnings: []
      },
      conceptMastery: feedback.detailedFeedback?.conceptMastery || {},
      correctSolution: feedback.detailedFeedback?.correctSolution,
      createdAt: new Date(),
      generatedAt: new Date(),
      // Legacy fields for compatibility
      bestPractices: feedback.detailedFeedback?.whatWorked || [],
      improvements: feedback.detailedFeedback?.nextSteps || []
    } as ExerciseFeedback : undefined;

    return {
      exerciseTitle: exercise.title,
      exerciseDescription: exercise.prompt,
      concepts: exercise.concepts,
      learningMode,
      programmingLanguage,
      currentCode,
      feedback: contextFeedback,
      miniCoursGenerated,
      correctionViewed,
      guidedMode
    };
  };

  const toggleGuidedMode = () => {
    const next = !guidedMode;
    setGuidedMode(next);
    if (next) {
      trackEvent('guided_mode_enabled', { exerciseId: exercise.id });
      setSuggestions(['Par où je commence ? Donne-moi la première étape.']);
    } else {
      generateInitialSuggestions();
    }
  };

  const sendMessage = async (message: string) => {
    if (!session || !message.trim()) return;

    try {
      setIsLoading(true);
      setError(null);
      setCurrentMessage('');
      
      if (!user?.id) {
        setError('Vous devez être connecté pour envoyer un message');
        return;
      }
      
      const context = buildChatContext();
      
      const result = await ChatService.sendMessage(
        session.id,
        exercise.id,
        message.trim(),
        context
      );

      // Add both messages to the chat
      setMessages(prev => [...prev, result.userMessage, result.assistantMessage]);
      setSuggestions(result.suggestions);
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de l\'envoi du message');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setCurrentMessage(suggestion);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(currentMessage);
    }
  };

  const formatTimestamp = (timestamp: Date) => {
    return new Date(timestamp).toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderMessage = (message: ChatMessage) => {
    const isUser = message.role === 'user';
    
    return (
      <div key={message.id} className={`chat-message ${isUser ? 'user' : 'assistant'}`}>
        <div className="message-header">
          <div className="message-avatar">
            {isUser ? '👤' : '🤖'}
          </div>
          <div className="message-info">
            <span className="message-sender">
              {isUser ? 'Vous' : 'Coach'}
            </span>
            <span className="message-time">
              {formatTimestamp(message.timestamp)}
            </span>
          </div>
        </div>
        <div className="message-content">
          {isUser ? (
            message.content
          ) : (
            <ReactMarkdown
              components={{
                // Custom components for better styling
                p: ({ children }) => <p>{children}</p>,
                strong: ({ children }) => <strong>{children}</strong>,
                em: ({ children }) => <em>{children}</em>,
                code: ({ children }) => <code>{children}</code>,
                pre: ({ children }) => <pre>{children}</pre>,
                blockquote: ({ children }) => <blockquote>{children}</blockquote>,
                ul: ({ children }) => <ul>{children}</ul>,
                ol: ({ children }) => <ol>{children}</ol>,
                li: ({ children }) => <li>{children}</li>,
                h1: ({ children }) => <h1>{children}</h1>,
                h2: ({ children }) => <h2>{children}</h2>,
                h3: ({ children }) => <h3>{children}</h3>,
                h4: ({ children }) => <h4>{children}</h4>,
                a: ({ children, href }) => <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>,
              }}
            >
              {message.content}
            </ReactMarkdown>
          )}
        </div>
      </div>
    );
  };

  // Always rendered (preloaded session) : hidden off-screen via CSS when closed,
  // so the slide-in transition actually plays
  return (
    <div className={`pedagogical-chatbot ${isOpen ? 'open' : ''}`}>
      <div className="chatbot-header">
        <div className="header-info">
          <h3>🎓 Coach de code</h3>
          <p>Posez vos questions sur l'exercice "{formatExerciseTitle(exercise.title)}"</p>
        </div>
        <button className="close-button" onClick={onClose}>
          ✕
        </button>
      </div>

      <div className="chatbot-content">
        {error && (
          <div className="error-message">
            <span>⚠️ {error}</span>
            <button onClick={() => setError(null)}>✕</button>
          </div>
        )}

        <div className="messages-container" ref={messagesContainerRef}>
          {messages.length === 0 && !isLoading && (
            <div className="welcome-message">
              <div className="welcome-avatar">🤖</div>
              <div className="welcome-text">
                <h4>Bonjour ! Je suis votre Coach de code.</h4>
                {mode === 'review' ? (
                  <>
                    <p>Vous consultez un exercice précédemment résolu. Je peux vous aider à :</p>
                    <ul>
                      <li>Analyser votre solution</li>
                      <li>Expliquer les concepts utilisés</li>
                      <li>Identifier les bonnes pratiques</li>
                      <li>Suggérer des améliorations</li>
                    </ul>
                  </>
                ) : (
                  <>
                    <p>Je suis là pour vous aider avec cet exercice{isGodotMode ? ' Godot' : isPythonJsMode ? '' : ' Unity'}. N'hésitez pas à me poser des questions sur :</p>
                    <ul>
                      <li>Les concepts de programmation</li>
                      <li>{isGodotMode ? "L'API Godot (nodes, signaux...)" : isPythonJsMode ? 'Les fonctions du langage' : "L'API Unity"}</li>
                      <li>Le débogage de votre code</li>
                      <li>Les bonnes pratiques</li>
                    </ul>
                  </>
                )}
              </div>
            </div>
          )}

          {messages.map(renderMessage)}
          
          {isLoading && (
            <div className="chat-message assistant loading">
              <div className="message-header">
                <div className="message-avatar">🤖</div>
                <div className="message-info">
                  <span className="message-sender">Coach</span>
                </div>
              </div>
              <div className="message-content">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* B4 — mode « explication guidée » : toggle de dernier recours + bandeau quand actif */}
        <div className={`guided-bar ${guidedMode ? 'active' : ''}`}>
          {guidedMode ? (
            <>
              <span className="guided-badge">🧭 Mode guidé</span>
              <span className="guided-hint">une étape à la fois — dites-moi quand c'est fait</span>
              <button className="guided-toggle" onClick={toggleGuidedMode}>
                Quitter
              </button>
            </>
          ) : (
            <>
              <button
                className="guided-toggle"
                onClick={toggleGuidedMode}
                title="Le Coach vous guide instruction par instruction, en vous montrant où écrire dans l'éditeur"
              >
                🧭 Guide-moi pas à pas
              </button>
              <span className="guided-hint">si vous êtes vraiment bloqué·e</span>
            </>
          )}
        </div>

        {suggestions.length > 0 && (
          <div className="suggestions-bar">
            <button
              className="suggestions-toggle"
              onClick={() => setSuggestionsCollapsed(prev => !prev)}
              title={suggestionsCollapsed ? 'Afficher les suggestions' : 'Masquer les suggestions'}
            >
              {suggestionsCollapsed ? '💡' : '▾'}
            </button>
            {!suggestionsCollapsed && (
              <div className="suggestions-scroll">
                {suggestions.map((suggestion, index) => (
                  <button
                    key={index}
                    className="suggestion-button"
                    title={suggestion}
                    onClick={() => handleSuggestionClick(suggestion)}
                    disabled={isLoading}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="input-container">
          <textarea
            ref={inputRef}
            value={currentMessage}
            onChange={(e) => setCurrentMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Tapez votre question ici... (Entrée pour envoyer, Shift+Entrée pour nouvelle ligne)"
            disabled={isLoading}
            rows={1}
          />
          <button
            className="send-button"
            onClick={() => sendMessage(currentMessage)}
            disabled={isLoading || !currentMessage.trim()}
          >
            {isLoading ? '⏳' : '📤'}
          </button>
        </div>
      </div>
    </div>
  );
}; 